import type { ReturnMode } from "@babackapp/return-widget-components/types";

export type ReturnModeAvailability = {
  hasCarrier: boolean;
  hasStoreReturns: boolean;
  hasRetractation: boolean;
  hasExchangeItem: boolean;
  isMerchant: boolean;
};

const RETURN_MODE_ORDER: ReturnMode[] = ["carrier", "store", "retractation"];

/**
 * Modes offered on the return-mode screen, in display order. The first one is
 * the form default, so the list never comes back empty.
 *
 * Without a carrier the carrier mode sends the customer off with no label,
 * which is what the retractation mode does while also flagging the return as a
 * retractation (14-day window, eligible country). The retractation mode then
 * takes its place (BBS-385). With neither, the carrier mode stays: it is the
 * only way to ship the parcel back.
 */
export function resolveReturnModes({
  hasCarrier,
  hasStoreReturns,
  hasRetractation,
  hasExchangeItem,
  isMerchant,
}: ReturnModeAvailability): ReturnMode[] {
  const isOffered: Record<ReturnMode, boolean> = {
    carrier: hasCarrier || !hasRetractation,
    store: hasStoreReturns && (!hasExchangeItem || isMerchant),
    retractation: hasRetractation,
  };
  return RETURN_MODE_ORDER.filter((mode) => isOffered[mode]);
}

export type StoreReturnState = {
  /** A rule refuses the store: greyed out with its reason. */
  isRefused: boolean;
  /** The answer for the current basket is still on its way: not selectable yet. */
  isPending: boolean;
};

/**
 * Combines the two `OVERRIDE_STORE_RETURN` answers (BBS-418). The basket one
 * (compute-return-fees, `undefined` until it is known for the current basket)
 * is evaluated like the creation and wins as soon as it is there. Until then
 * the order-load one decides, and the store stays out of reach while the
 * basket answer is being fetched. A failed fetch falls back to the order load;
 * the creation guard still has the last word.
 */
export function resolveStoreReturnState({
  orderLoadAnswer,
  basketAnswer,
  isBasketAnswerPending,
}: {
  orderLoadAnswer: boolean | null | undefined;
  basketAnswer: boolean | null | undefined;
  isBasketAnswerPending: boolean;
}): StoreReturnState {
  if (basketAnswer !== undefined) {
    return { isRefused: basketAnswer === false, isPending: false };
  }
  return {
    isRefused: orderLoadAnswer === false,
    isPending: isBasketAnswerPending,
  };
}

/**
 * Mode the form starts on, and falls back to: the first offered one, skipping
 * a store that a rule refuses. Such a store stays on screen, greyed out, but
 * is never selected (BBS-418).
 */
export function resolveDefaultReturnMode(
  modes: ReturnMode[],
  isStoreRefused: boolean,
): ReturnMode {
  return (
    modes.find((mode) => mode !== "store" || !isStoreRefused) ?? modes[0]
  );
}
